import axios, { AxiosError, AxiosHeaders, AxiosResponse } from 'axios'
import { createFlagsClient } from '@labrav/flags'
import devFlags from '../../flags/dev/flags.json'
import { isTestEnvironment } from '../utils/testing'
import {
  JWTRegex,
  crmServiceUrl,
  engineServiceUrl,
  engineServiceV2Url,
  fetchAwsDimensionsCategoriesListUrl,
  fetchCountriesListS3Url,
  identityServiceUrl,
  labravAWSEngineServiceUrl,
  marketplaceServiceUrl,
  oktaDomain,
  flagsUrl,
  flagsCacheTTLSeconds,
  serviceId,
  serviceEnvironment,
} from '../utils/constants'
import {
  G2ReviewResponseType,
  AccountData,
  PatchPartnerAWSDetails,
  UpdateG2Review,
} from '../modules/partner/action'
import { AccountData as AccountDataSelection } from '../modules/accountSelection/types'
import { errorLogger as newrelicErrLogger } from '../utils/ErrorHandler'
import { reportFrontendError } from '../../telemetry'
import {
  AuthProvider,
  GetUsersParams,
} from '../../oppsync/modules/userList/userListActionTypes'
import { UserState } from '../../oppsync/modules/userList/reducer'

import { GetPartnerObjectsParams } from '../../oppsync/modules/salesPipeline/partnerObjects/actions'
import { CRMInfo } from '../modules/onboarding/crmAuth/action'
import {
  getSecureKey,
  getServiceNameFromUrl,
  isAuth0ProviderSecureKey,
} from '../utils/helperFunctions'
import { FeatureFlags } from '../utils/featureFlags'
import { canvasAuthService } from '../services/CanvasAuthService'
import { UserData } from '../components/Register/RegisterUI'
import { IdentityServiceUser, UserProfile } from '../modules/userProfile/action'
import { snakeize } from 'casing'
import { SyncObjectType } from '../../oppsync/modules/salesPipeline/syncObject/reducer'
import { CloudType } from '@labrav/react-components'
import { ProductType } from '../modules/partner/reducer'
import {
  AUTH0_TOKEN_KEY,
  isInvalidAccessTokenResponse,
  refreshAuth0AccessToken,
} from './auth'

export const API = axios.create({ timeout: 30000 })

/** Presigned S3 POST/PUT must not carry Labra auth headers — S3 rejects them with 400. */
export const isS3PresignedRequestUrl = (url: string): boolean =>
  /\.s3(\.[a-z0-9-]+)?\.amazonaws\.com/i.test(url)

const flagsClient = createFlagsClient({
  service: serviceId,
  env: serviceEnvironment,
  flagsUrl,
  cacheTtlSeconds: flagsCacheTTLSeconds,
  flagsJson: import.meta.env.DEV ? devFlags : undefined,
})

const sameOriginApiEnabled: Promise<boolean> = flagsClient
  .getFlagValue(FeatureFlags.SAME_ORIGIN_API)
  .catch(() => false)

export const setOnboardingWelcome = (isWelcome: boolean) => {
  try {
    localStorage.setItem('onboardingWelcome', JSON.stringify(isWelcome))
  } catch (err) {
    console.warn('localStorage is not available.')
    newrelicErrLogger(err as Error, {
      message: err,
    })
    return undefined
  }
}

if (!isTestEnvironment()) {
  API.interceptors.request.use(async config => {
    const originalRequestUrl = config.url || ''

    if ((await sameOriginApiEnabled) && config.url) {
      config.url = config.url.replace(
        /^https?:\/\/(qa\.|stg\.)?api\.labra\.io\//,
        `${window.location.origin}/api/`
      )
    }

    const requestUrl = config.url || originalRequestUrl
    if (isS3PresignedRequestUrl(requestUrl)) {
      const headers = AxiosHeaders.from(config.headers)
      headers.delete('Authorization')
      headers.delete('Internal-Authorization')
      config.headers = headers
      return config
    }

    const serviceName =
      getServiceNameFromUrl(originalRequestUrl) ||
      getServiceNameFromUrl(config.url || '')
    const secureKey = getSecureKey()

    // HubSpot / Collab Room / embed JWT in URL must win over Auth0 or Canvas tokens.
    // Otherwise aws_engine returns 403 "invalid access token" when Auth0 is in localStorage.
    if (secureKey && new RegExp(JWTRegex).test(secureKey)) {
      if (isAuth0ProviderSecureKey(secureKey)) {
        config.headers['Authorization'] = `Bearer ${secureKey}`
        return config
      }

      if (serviceName) {
        config.headers['Internal-Authorization'] =
          `Bearer-${serviceName} ${secureKey}`
        return config
      }
    }

    // Check for Salesforce Canvas authentication
    // CanvasAuthService is only initialized in /external-apps/salesforce/ routes
    const canvasJWT = canvasAuthService.getAccessToken()
    if (canvasJWT) {
      // Check environment variable to determine header type
      // VITE_SALESFORCE_USE_AUTH0=true: Use standard Authorization header (for testing with Auth0 tokens)
      // VITE_SALESFORCE_USE_AUTH0=false: Use Internal-Authorization header (for production with backend JWTs)
      const useAuth0 = import.meta.env.VITE_SALESFORCE_USE_AUTH0 === 'true'

      if (useAuth0) {
        // Testing mode: Use standard Authorization header with Auth0 token
        config.headers['Authorization'] = `Bearer ${canvasJWT}`
      } else {
        // Production mode: Use Internal-Authorization with service name
        if (serviceName) {
          config.headers['Internal-Authorization'] =
            `Bearer-${serviceName} ${canvasJWT}`
        }
      }

      return config
    }

    if (!Object.hasOwn(config.headers, 'Authorization')) {
      const token = localStorage.getItem(AUTH0_TOKEN_KEY)
      config.headers['Authorization'] = `Bearer ${token}`
    }

    return config
  })

  API.interceptors.response.use(
    res => {
      return res
    },
    async (error: AxiosError) => {
      const config = error.config as typeof error.config & {
        _authRetry?: boolean
      }
      if (
        config &&
        isInvalidAccessTokenResponse(
          error.response as AxiosResponse<ErrorResponse & { message?: string }>
        ) &&
        !config._authRetry
      ) {
        const refreshed = await refreshAuth0AccessToken()
        if (refreshed) {
          config._authRetry = true
          config.headers = AxiosHeaders.from(config.headers)
          config.headers.set('Authorization', `Bearer ${refreshed}`)
          return API(config)
        }
      }

      const requestId = error.response?.headers['x-request-id']
      const { url: requestURL, method: requestMethod } =
        error.response?.config || {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // newrelicErrLogger((error.response?.data as any)?.message || error, {
      //   requestId: requestId || 'No Request Id',
      //   requestURL: requestURL || 'NO Request URL',
      //   requestMethod: requestMethod || 'NO Request Method',
      // })
      const requestData = error.response?.config?.data ?? error.config?.data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiMessage = (error.response?.data as any)?.message ?? ''
      reportFrontendError('api.error', error, {
        'http.status': String(error.response?.status ?? 'network_error'),
        'http.url': requestURL ?? 'unknown',
        'http.method': requestMethod?.toUpperCase() ?? 'unknown',
        'http.request_id': requestId ?? '',
        'http.error_message': apiMessage,
        'http.request_body':
          typeof requestData === 'string'
            ? requestData
            : JSON.stringify(requestData ?? null),
        'http.response_body': JSON.stringify(error.response?.data ?? null),
      })
      return Promise.reject(error)
    }
  )
}

//Fetch list of CRMS
export const fetchCRMs = () => API.get(`${crmServiceUrl}/auth_defintions`)

//GET Partner Data
export const getPartnerDataFromEngineService = (
  partnerId: string,
  fetchOktaUser: boolean
) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}`, {
    params: {
      fetch_okta_users: fetchOktaUser,
    },
  })

//GET Partner Data from Identity Service
export const getPartnerDataFromIdentityService = (
  partnerId: string,
  fetchOwner: boolean
) =>
  API.get(`${identityServiceUrl}/partners/${partnerId}`, {
    params: fetchOwner && {
      include: 'PARTNER_OWNER',
    },
  })

//POST CRM Auth
export const postCRMAuthData = (authData: unknown, partnerId: string) =>
  API.post(`${engineServiceUrl}/partners/${partnerId}/crms`, authData, {
    params: {
      check_connection: true,
    },
  })
export const patchCRMAuthData = (crmId: string, authData: unknown) =>
  API.patch(`${crmServiceUrl}/crms/${crmId}`, authData, {
    params: {
      check_connection: true,
    },
  })

//GET G2 review status
export const getG2ReviewData = async (
  partnerId: string
): Promise<G2ReviewResponseType[]> => {
  const response = await API.get(
    `${identityServiceUrl}/partners/${partnerId}/customer-reviews`
  )

  const reviewData: G2ReviewResponseType[] = response.data.results.map(
    (item: any) => ({
      id: item.id,
      completed: item.completed,
      openedAt: item.opened_at,
      reviewType: item.review_type,
      partnerId: item.partner_id,
    })
  )

  return reviewData
}
//PUT App Mappings
export function putAppMappings(partnerId: string, CRMInfo: CRMInfo) {
  API.put(`${engineServiceUrl}/partners/${partnerId}/app_mappings`, CRMInfo)
}
//GET CRM Auth data
export const getCRMAuth = (crmId: string) =>
  API.get(`${crmServiceUrl}/crms/${crmId}?includes=creds,crm_app_creds`)

export const getCRMType = (crmId: string) =>
  API.get(`${crmServiceUrl}/crms/${crmId}`)

export const fetchCRMTables = (crm_id: string) =>
  API.get(`${crmServiceUrl}/crms/${crm_id}/tables`)

export const fetchCRMTableFields = (
  crmId: string,
  tableName: string,
  awsFieldsVersion: string
) =>
  API.get(`${crmServiceUrl}/crms/${crmId}/tables/${tableName}/fields`, {
    params: {
      awsFieldsVersion: awsFieldsVersion,
    },
  }).then(({ data }) => data)

export const fetchACEFields = (objectType: string, aceFieldsVersion: string) =>
  API.get(`${engineServiceUrl}/ace-fields`, {
    params: {
      object_type: objectType,
      ace_fields_version: aceFieldsVersion,
    },
  })

export const fetchOnboarding = (partnerId: string) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/onboarding`)

export const fetchMappingData = (partnerId: string) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/mappings`)

export const fetchPartnersStatitics = () =>
  API.get(`${engineServiceUrl}/partners-statistics`)

export const fetchAllPartners = (pageSize: number, pageNumber: number) =>
  API.get(`${engineServiceUrl}/partners`, {
    params: {
      limit: pageSize,
      offset: pageNumber,
      fetch_okta_users: true,
      onboardings: true,
      includes: 'creds,crm_app_creds',
    },
  })

export const fetchAllPartnersFromIdentityService = (
  pageSize: number,
  pageNumber: number
) =>
  API.get(`${identityServiceUrl}/partners`, {
    params: {
      limit: pageSize,
      offset: pageNumber,
      includes: 'PARTNER_OWNER',
    },
  })

export const searchPartnerList = (
  searchQuery: string,
  pageSize: number,
  pageNumber: number
) => {
  return API.get(`${engineServiceUrl}/partners`, {
    params: {
      limit: pageSize,
      offset: pageNumber,
      user_name: searchQuery,
      fetch_okta_users: true,
      onboardings: true,
      includes: 'creds,crm_app_creds',
    },
  })
}

export const searchPartnerListFromIdentityService = (
  searchQuery: string,
  pageSize: number,
  pageNumber: number
) => {
  return API.get(`${identityServiceUrl}/partners`, {
    params: {
      limit: pageSize,
      offset: pageNumber,
      query: searchQuery,
      includes: 'PARTNER_OWNER',
    },
  })
}

export const fetchUsers = (args: GetUsersParams) => {
  const { partnerId, pageSize, pageNumber } = args
  return API.get(`${engineServiceUrl}/partners/${partnerId}/users`, {
    params: {
      limit: pageSize,
      page_number: pageNumber,
    },
  })
}

export const fetchUsersFromIdentityService = (args: GetUsersParams) => {
  const { partnerId, pageSize, pageNumber } = args
  return API.get(`${identityServiceUrl}/partners/${partnerId}/users`, {
    params: {
      take: pageSize,
      skip: ((pageNumber || 1) - 1) * (pageSize || 10),
    },
  })
}

export async function getAllPartnerUserEmails(
  partnerId: string,
  pageSize = 200
): Promise<string[]> {
  let allEmails: string[] = []
  let pageNumber = 1

  try {
    while (true) {
      const { data } = await fetchUsersFromIdentityService({
        partnerId,
        pageSize,
        pageNumber,
        authProvider: AuthProvider.AUTH0,
      })

      const emails: string[] = (data?.data || [])
        .map((user: { email?: string }) => user?.email)
        .filter(Boolean)

      allEmails = [...allEmails, ...emails]

      if (emails.length < pageSize) break
      pageNumber++
    }

    return allEmails
  } catch (error) {
    reportFrontendError('api.error', error as Error, {
      partner_id: partnerId,
    })
    return []
  }
}

export const fetchPartnerObjects = (args: GetPartnerObjectsParams) => {
  const {
    partnerId,
    objectType,
    pageSize,
    pageNumber,
    syncStatus,
    objectsIds,
  } = args

  return API.get(`${engineServiceUrl}/partners/${partnerId}/objects`, {
    params: {
      object_type: objectType,
      limit: pageSize,
      page_number: pageNumber,
      last_sync_state: syncStatus,
      object_ids: objectsIds,
    },
  })
}

export const fetchPartnerObject = (
  partnerId: string,
  objectId: string,
  relations: string,
  objectType: CRMObjectType
) => {
  return API.get(
    `${engineServiceUrl}/partners/${partnerId}/objects/${objectId}`,
    {
      params: {
        relations,
        object_type: objectType,
      },
    }
  )
}

export const fetchPartnerObjectsStatistics = (partnerId: string) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/statistics`)

export const fetchSyncHistory = (
  partnerId: string,
  pageSize: number,
  pageNumber: number
) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/syncs`, {
    params: {
      limit: pageSize,
      page_number: pageNumber,
    },
  })

export const fetchLastSyncDetails = (partnerId: string) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/syncs`, {
    params: {
      limit: 1,
      page_number: 1,
    },
  })
export const getCloudConfiguration = (partnerId: string) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/cloud-configuration`)
export const postSupportData = (partnerId: string, data: unknown) =>
  API.post(`${engineServiceUrl}/partners/${partnerId}/support-tickets`, data)

export const saveCrmAceMapping = (
  partnerId: string,
  data: unknown,
  updateStatus = true
) =>
  API.put(`${engineServiceUrl}/partners/${partnerId}/mappings`, data, {
    params: {
      update_status: updateStatus,
    },
  })

export const postPartnerMappings = (partnerId: string) =>
  API.post(`${engineServiceUrl}/partners/${partnerId}/mappings`)

export const patchUserProfile = (
  partnerId: string,
  oktaUserId: string,
  profile: UserProfile
) =>
  API.patch(
    `${engineServiceUrl}/partners/${partnerId}/profiles/${oktaUserId}`,
    snakeize(profile)
  )

export const patchUserProfileIdentityService = (
  partnerId: string,
  userId: string,
  profile: UserProfile
) =>
  API.patch(
    `${identityServiceUrl}/partners/${partnerId}/users/${userId}`,
    profile
  )

export const fetchUserProfile = (
  partnerId: string,
  userId: string
): Promise<IdentityServiceUser> =>
  API.get(`${identityServiceUrl}/partners/${partnerId}/users/${userId}`).then(
    ({ data }) => data as IdentityServiceUser
  )

export const updateUserFromIdentityService = (
  partnerId: string,
  data: unknown,
  userId?: string
) =>
  API.patch(`${identityServiceUrl}/partners/${partnerId}/users/${userId}`, data)

export const loginWithCredentials = (username: string, password: string) => {
  return API.post(`https://${oktaDomain}/api/v1/authn`, {
    username: username,
    password: password,
  })
}
export const registerWithCredentials = (data: UserData) => {
  return API.post(`${engineServiceV2Url}/partners`, data)
}

export const createAccount = (data: Partial<AccountDataSelection>) => {
  let updatedData
  updatedData = {
    ...data,
    product: data?.product?.toUpperCase() || 'FLYOUT',
  }
  return API.post(`${identityServiceUrl}/accounts`, updatedData)
}

export const getAccounts = (
  partnerId: string,
  cloudType?: CloudType,
  productType?: ProductType
) => {
  const params = new URLSearchParams({ partner_id: partnerId })

  if (cloudType) {
    params.append('cloud_type', cloudType.toString())
  }

  if (productType) {
    params.append('product_type', productType.toString())
  }

  return API.get(`${identityServiceUrl}/accounts?${params.toString()}`)
}

export const getAccountByCloudAndProduct = (
  accountId: string,
  cloudType: string,
  productType: string
) => {
  return API.get(
    `${identityServiceUrl}/accounts/${accountId}/cloud/${cloudType}/product/${productType}`
  )
}

export const updateAccountByCloudAndProduct = (
  accountId: string,
  cloudType: string,
  productType: string,
  data: Partial<AccountDataSelection>
) => {
  return API.patch(
    `${identityServiceUrl}/accounts/${accountId}/cloud/${cloudType}/product/${productType}`,
    data
  )
}

export const deleteAccountByCloudAndProduct = (
  accountId: string,
  cloudType: string,
  productType: string
) => {
  return API.delete(
    `${identityServiceUrl}/accounts/${accountId}/cloud/${cloudType}/product/${productType}`
  )
}
export const recoverPasswordWithEmail = (username: string) => {
  return API.post(`https://${oktaDomain}/api/v1/authn/recovery/password`, {
    username: username,
    factorType: 'EMAIL',
  })
}
export const patchCRMSchema = (
  crmId: string,
  crmSchema: Record<string, never>
) => API.patch(`${crmServiceUrl}/crms/${crmId}`, { crm_schema: crmSchema })

export const updateUserData = (
  partnerId: string,
  userId: string,
  data: UserState
) =>
  API.patch(`${engineServiceUrl}/partners/${partnerId}/users/${userId}`, data)

export const patchPartnerEngineService = (
  partnerId: string,
  data: Partial<AccountData>
) => API.patch(`${labravAWSEngineServiceUrl}/partners/${partnerId}`, data)

export const patchPartnerOldEngineService = (
  partnerId: string,
  data: Partial<AccountData>
) => API.patch(`${engineServiceUrl}/partners/${partnerId}`, data)

export const patchPartnerIdentityService = (
  partnerId: string,
  data: Partial<AccountData>
) => API.patch(`${identityServiceUrl}/partners/${partnerId}`, data)

export const patchPartnerAWSDetails = (
  partnerId: string,
  data: PatchPartnerAWSDetails
) => API.patch(`${engineServiceUrl}/partners/${partnerId}/aws-details`, data)

export const uploadPartnerCloudConfig = (
  partnerId: string,
  data: FormData,
  progressCallback?: (progressEvent: any) => void
) =>
  API.put(
    `${engineServiceUrl}/partners/${partnerId}/cloud-configuration`,
    data,
    {
      onUploadProgress: progressCallback,
    }
  )

export const triggerManualSync = (partnerId: string) =>
  API.post(`${engineServiceUrl}/partners/${partnerId}/syncs`)

export const fetchSyncReport = (
  partnerId: string,
  pageSize: number,
  pageNumber: number
) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/sync-results`, {
    params: {
      limit: pageSize,
      page_number: pageNumber,
    },
  })

export const getCheckS3Connection = (
  partnerId: string,
  env: 'production' | 'staging'
) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/check-s3connection`, {
    params: {
      environment: env,
    },
  })

export const postAddUsersData = (data: unknown, partnerId: string) =>
  API.post(`${engineServiceUrl}/partners/${partnerId}/users`, data)

export const postAddNewPartnerData = (data: unknown) =>
  API.post(`${identityServiceUrl}/partners`, data)

export const inviteUsersIdentityService = (data: unknown, partnerId: string) =>
  API.post(`${identityServiceUrl}/partners/${partnerId}/invitations`, data)

export const postResendInvite = (partnerId: string, userId: string) =>
  API.post(
    `${engineServiceUrl}/partners/${partnerId}/users/${userId}/lifecycle/reactivate`
  )

export const resendInvitationIdentityService = (
  partnerId: string,
  userId: string
) =>
  API.post(
    `${identityServiceUrl}/partners/${partnerId}/users/${userId}/email_verifications`
  )

export const resetMfaIdentityService = (partnerId: string, userId: string) =>
  API.post(
    `${identityServiceUrl}/partners/${partnerId}/users/${userId}/reset_mfa`
  )

export const deleteUser = (partnerId: string, userId: string) =>
  API.delete(`${engineServiceUrl}/partners/${partnerId}/users/${userId}`)

export const deleteUserIdenityService = (partnerId: string, userId: string) =>
  API.delete(`${identityServiceUrl}/partners/${partnerId}/users/${userId}`)

export const deleteSyncsData = (partnerId: string) =>
  API.delete(`${engineServiceUrl}/partners/${partnerId}/syncs`)

export const deleteCRM = (partnerId: string, crmId: string) =>
  API.delete(`${engineServiceUrl}/partners/${partnerId}/crms/${crmId}`)

export const deleteCRMFromCRMService = (crmId: string) =>
  API.delete(`${crmServiceUrl}/crms/${crmId}`)

export const postCheckCrmConnection = (crm_id: string) =>
  API.post(`${crmServiceUrl}/crms/${crm_id}/check-salesforce-app-connection`)

export const fetchAwsCountriesList = () => axios.get(fetchCountriesListS3Url)

export const fetchAwsDimensionsCategoriesList = () =>
  axios.get(fetchAwsDimensionsCategoriesListUrl)

export const getG2ReviewUrl = (partnerId: string, userId: string) =>
  API.get(
    `${identityServiceUrl}/partners/${partnerId}/users/${userId}/review_urls`
  )

export const putCustomerReviewG2 = (partnerId: string, data: UpdateG2Review) =>
  API.put(
    `${identityServiceUrl}/partners/${partnerId}/customer-reviews/g2`,
    data
  )

export const patchUserWalkthrough = (
  partnerId: string,
  userId: string,
  data: Record<string, any>
) =>
  API.patch(`${identityServiceUrl}/partners/${partnerId}/users/${userId}`, data)

export const fetchAPIKeys = (partnerId: string) =>
  API.get(`${identityServiceUrl}/partners/${partnerId}/public_credentials`)

export const createAPIKey = (partnerId: string, keyData: any) =>
  API.post(
    `${identityServiceUrl}/partners/${partnerId}/public_credentials`,
    keyData
  )

export const updateAPIKey = (partnerId: string, keyId: string, keyData: any) =>
  API.patch(
    `${identityServiceUrl}/partners/${partnerId}/public_credentials/${keyId}`,
    keyData
  )

export const deleteAPIKey = (partnerId: string, keyId: string) =>
  API.delete(
    `${identityServiceUrl}/partners/${partnerId}/public_credentials/${keyId}`
  )

export const fetchAPIKeysLogs = (partnerId: string) =>
  API.get(
    `${identityServiceUrl}/partners/${partnerId}/public_credentials_activity_logs`
  )

export const getWorkatoIframeUrlApi = ({
  workatoCustomerId,
  path,
}: {
  workatoCustomerId: string
  path: string
}) => {
  return API.get(
    `${marketplaceServiceUrl}/v1/workflows/${workatoCustomerId}/${path}`,
    {}
  )
}
